
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import { Options, Vue } from "vue-class-component";
import store from "@/store";
import {
  formatYenApprox,
  llmUsageLimitLoweringConfirmMessage,
  resolveLlmUsageLimitMicroUsd,
  LlmUsageLimits,
  LlmUsageLimitEntry,
  LlmUsageLimitValue
} from "@/entities/llm_usage_limit";
import { SubscriptionType } from "@/entities/school";
import { setSchoolLlmUsageLimit } from "@/api/llmUsageLimit";

@Options({
  components: {
    MBaseModal,
    MButton,
    MTextArea
  },
  emits: ["close", "updated"],
  props: {
    schoolId: String,
    llmUsageLimits: Array,
    subscriptionType: String,
    limits: Object,
    currentText: String,
    currentSourceText: String
  }
})
export default class MscLlmUsageLimitModal extends Vue {
  schoolId = "";
  llmUsageLimits: LlmUsageLimitEntry[] = [];
  subscriptionType?: SubscriptionType;
  limits!: LlmUsageLimits;
  currentText = "";
  currentSourceText = "";

  kind: "default" | "amount" | "none" = "amount";
  dollars = "";
  timing: "now" | "at" = "now";
  effectiveFromLocal = "";
  reason = "";

  get amountMicroUsd(): number | null {
    if (this.dollars === "") return null;
    const dollars = Number(this.dollars);
    if (!Number.isFinite(dollars) || dollars < 0) return null;
    return Math.round(dollars * 1_000_000);
  }

  // NOTE: datetime-local の値は JST として解釈する
  get effectiveFrom(): number | null {
    if (this.timing === "now" || this.effectiveFromLocal === "") return null;
    const ms = new Date(`${this.effectiveFromLocal}:00+09:00`).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }

  get canSubmit(): boolean {
    if (this.reason.trim() === "") return false;
    if (this.kind === "amount" && this.amountMicroUsd === null) return false;
    if (this.timing === "at" && this.effectiveFrom === null) return false;
    return true;
  }

  yenText(microUsd: number): string {
    return formatYenApprox(microUsd);
  }

  close() {
    this.$emit("close");
  }

  buildLimit(): LlmUsageLimitValue {
    if (this.kind === "amount") {
      return { type: "amount", microUsd: this.amountMicroUsd as number };
    }
    return { type: this.kind };
  }

  loweringMessage(): string | null {
    const now = Math.floor(Date.now() / 1000);
    const at = this.effectiveFrom ?? now;
    const currentMicroUsd = resolveLlmUsageLimitMicroUsd(
      {
        llmUsageLimits: this.llmUsageLimits,
        subscriptionType: this.subscriptionType,
        at
      },
      this.limits
    );
    let nextMicroUsd: number | null;
    if (this.kind === "amount") {
      nextMicroUsd = this.amountMicroUsd;
    } else if (this.kind === "none") {
      nextMicroUsd = null;
    } else {
      nextMicroUsd = resolveLlmUsageLimitMicroUsd(
        {
          llmUsageLimits: [],
          subscriptionType: this.subscriptionType,
          at
        },
        this.limits
      );
    }
    return llmUsageLimitLoweringConfirmMessage({
      currentMicroUsd,
      nextMicroUsd,
      effectiveFrom: this.effectiveFrom
    });
  }

  async confirm() {
    if (!this.canSubmit) return;
    const message = this.loweringMessage();
    if (message !== null && !window.confirm(message)) return;

    store.commit("START_LOADING", "利用額の上限を変更中...");
    try {
      const rows = await setSchoolLlmUsageLimit({
        schoolId: this.schoolId,
        limit: this.buildLimit(),
        ...(this.effectiveFrom !== null
          ? { effectiveFrom: this.effectiveFrom }
          : {}),
        reason: this.reason.trim()
      });
      this.$emit("updated", rows);
    } catch (e) {
      alert(`利用額の上限の変更に失敗しました: ${(e as Error).message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }
}
